import AddFieldButton from "@app/objects/AddFieldButton"
import AddRecordButton from "@app/objects/AddRecordButton"
import AddRecordTableRow from "@app/objects/AddRecordTableRow"
import SummaryTableRow from "@app/grid/SummaryTableRow"
import ConfigFieldMenuButton from "@app/objects/ConfigFieldMenuButton"
import ViewFieldMenuButton from "@app/objects/ViewFieldMenuButton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@comps/ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip"
import { CompozzError, isCompozzError } from "@dto/CompozzError"
import { AddRightOption, EditRightAccess, ObjectRightType, ViewRightType, hasGlobalRightAccess, hasGlobalRightOption } from "@dto/Users"
import Field, { AggregationType, FieldType, columnAggregation, fieldTypeIcon } from "@dto/fields"
import { ObjectInfos } from "@dto/objects"
import { Period } from "@dto/period"
import { FieldColumn, Item, Line, MetaLine, areLinesEqual, extractFirstLinkPath, getSrcField } from "@dto/records"
import { summaryAggregationsByColumns, summaryCellRadius, summaryValueToString } from "@utils/summaries"
import { applyTableColumnSizing, ColumnSizes, columnId, getColumnMinSize, headerZIndex, migrateLegacyColumnSizing, toTableColumnSizing } from "@utils/columnSizing"
import { Group, LEFT, View } from "@dto/views"
import {
  Column,
  ColumnDef,
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  Header,
  Row,
  RowSelectionState,
  Table as TSTable,
  Updater,
  VisibilityState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { moveElement } from "@utils/arrays"
import { copyAll } from "@utils/copy"
import { GRAY_400_COLOR, LINE_HOVER_COLOR, RGBColor, WEEKEND_COLOR, toRGBColor } from "@utils/colors"
import { BASE_URL, DEFAULT_ORDER_INCREMENT, GROUP_BASE_COLOR, GROUP_ROW_HEIGHT, LINE_ROW_HEIGHT, MIN_COLUMN_WIDTH, SELECT_COLUMN_WIDTH } from "@utils/constants"
import { viewColumnSizingStorageId } from "@utils/localStorage"
import { columnErrorMessage, isColumnInError } from "@utils/columnErrors"
import { fieldToPath, getFieldByPathKeys, getPathKeys, isFieldValid, pathKey } from "@utils/model"
import { toastError } from "@utils/toasts"
import CompozzSpinner from "@comps/ui/CompozzSpinner"
import { TW_CompozzSpinnerContainer, TW_DataTable, TW_DataTableBorder, TW_DataTableGridHeader, TW_DataTableGridHeaderCell, TW_DataTableGridHeaderCellIcon, TW_DataTableGridHeaderCellText, TW_DataTableGridHeaderTH, TW_DataTableGridHeaderTR, TW_DataTableGroupFieldName, TW_DataTableGroupRow, TW_DataTableGroupRowIcon, TW_DataTableGroupRowText, TW_DataTableGroupRowTextContainer, TW_DataTableGroupRowTextEmpty, TW_DataTableSelectAllButton_not_selected, TW_DataTableSelectAllButton_selected, TW_DataTableSelectButton_not_selected, TW_DataTableSelectButton_selected, TW_DataTableTableBody, TW_DataTableTableBodyRow, TW_DataTableTableLastGroupRow, TW_DataTableTableLastGroupRowCell, minRowHeight } from "@utils/twClasses"
import { itemToString, valueToString } from "@utils/types"
import { useWindowDimensions } from "@utils/useWindowDimensions"
import { computeTextWidth, put } from "@utils/utils"
import { TFunction, t } from "i18next"
import { CheckSquare, ChevronDown, ChevronRight, Square } from "lucide-react"
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { textToHTML, translate } from "src/i18n/i18n"
import { useDataStore, useModelStore, useUserStore } from "src/store"
import { CellPosition, GridCell, getLastSelectionCell } from "./GridCell"
import { nextRowSelectionOnClick, visibleRecordRowIds } from "./rowRangeSelection"
import { CellFocusContext } from "./CellFocusContext"
import { CellFocusNotifyParams, createCellFocusStore, HandleCorner, syncCellFocus } from "./CellFocusStore"
import { dragScrollSpeed, revealGridCell, scrollableBounds, useScrollLeftKeptAcrossClear } from "./gridScroll"
import { ColumnResizer } from "./column-resizer"

interface DataTableProps {
  fieldColumns: FieldColumn[]
  lines: Line[]
  groups: Group[]
  searchText: string
  onStatusesChange: (statuses: Status[]) => void
  // Each new value brings the grid back to its first line
  scrollTopResetKey?: number
}

interface Status {
  label: string;
  value: any;
}

export function DataTable({
  fieldColumns,
  lines,
  groups,
  searchText,
  onStatusesChange,
  scrollTopResetKey,
}: DataTableProps) {


  const workspace = useModelStore(state => state.selectedWorkspace);
  const { t } = useTranslation();
  // Persisted column widths, keyed by a STABLE key (utils/columnSizing): the
  // TanStack sizing state (keyed by positional column id) is derived from
  // them below, once the columns are known.
  const [colSizes, setColSizes] = useState<ColumnSizes>({});
  const selectedView = useModelStore(state => state.selectedView);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const setRowSelection = useDataStore(state => state.setRowSelection);
  const rowSelection = useDataStore(state => state.rowSelection);
  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const userRole = useModelStore(state => state.userRole);
  const user = useUserStore(state => state.user);
  const [tableWidth, setTableWidth] = useState(0);
  const [isAddingRecord, setIsAddingRecord] = useState(false);
  const focus = useDataStore(state => state.focus);
  const focusMultiple = useDataStore(state => state.focusMultiple);
  const focusPos = useDataStore(state => state.focusPos);
  const selectionAnchor = useDataStore(state => state.selectionAnchor);
  const loadingData = useDataStore(state => state.loadingData);
  const viewSummaries = useDataStore(state => state.summaries);
  const setSelectionAnchor = useDataStore(state => state.setSelectionAnchor);
  const isSelectionHandleDraggingRef = useRef(false);
  // Number of group levels, read when a row checkbox is clicked to tell record
  // rows (depth === groups.length) from group rows. A ref rather than a column
  // dependency: `groups` is a new array on every render of ObjectModelView.
  const lineDepthRef = useRef(0);
  lineDepthRef.current = groups?.length ?? 0;
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const contextMenuPasteActionRef = useRef<(() => void) | null>(null);
  const contextMenuDeleteActionRef = useRef<(() => void) | null>(null);
  const [contextMenu, setContextMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    canPaste: false,
    canDelete: false,
  });

  // Virtualization setup
  const tableContainerRef = useRef<HTMLTableElement>(null);
  // Scroll container of the grid (both directions)
  const scrollerRef = useRef<HTMLDivElement>(null);

  const rgbWsColor = useMemo(() => toRGBColor(GROUP_BASE_COLOR), []);

  // Memoize expensive computations to prevent unnecessary recalculations
  const fields = useMemo(() => fieldColumns.map((fieldColumn) => getSrcField(fieldColumn)), [fieldColumns]);
  const fieldByKeys = useModelStore(state => state.fieldByKeys);
  const fieldByPathKeys = useMemo(() =>
    getFieldByPathKeys(selectedObjectInfos, selectedObjectInfos?.fields || [], relationByKeys, fieldByKeys),
    [selectedObjectInfos, relationByKeys, fieldByKeys]
  );
  const groupFields: Field[] = useMemo(() =>
    groups?.map((group: Group) => fieldByPathKeys.get(group.pathKey))?.filter((field) => field !== undefined) as Field[] || [],
    [groups, fieldByPathKeys]
  );

  // Debounced search text
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Search filtering
  const filteredLines = useMemo(() => {
    if (!debouncedSearchText || debouncedSearchText.trim() === "") {
      return lines;
    }
    const lowerSearch = debouncedSearchText.toLowerCase();

    const lineMatchesSearch = (line: Line): boolean => {
      return line.items.some((item, index) => {
        if (!item?.value) return false;
        const fieldColumn = fieldColumns[index];
        const field = fieldColumn ? getSrcField(fieldColumn) : null;
        if (!field) return false;

        const format = selectedView?.formats?.get(fieldColumn?.pathKey ?? "") ?? field?.format;

        const pathKey = fieldColumn?.pathKey ?? "";
        const aggregation = selectedView?.aggregations?.get(pathKey) ?? field?.aggregation;
        let strValue = itemToString(item, field, aggregation, format, t, "", fieldByKeys);
        // Replace all non-breaking spaces with regular spaces
        strValue = strValue.replaceAll(" ", " ");
        return strValue?.toLowerCase()?.includes(lowerSearch);
      });
    };

    const filterLineRecursive = (line: Line): Line | null => {
      const matchingChildren = line.children
        ?.map(filterLineRecursive)
        .filter((child): child is Line => child !== null) ?? null;

      const hasMatchingChildren = matchingChildren && matchingChildren.length > 0;
      const selfMatches = lineMatchesSearch(line);

      if (selfMatches || hasMatchingChildren) {
        return {
          ...line,
          children: hasMatchingChildren ? matchingChildren : line.children,
        };
      }
      return null;
    };

    return lines
      .map(filterLineRecursive)
      .filter((line): line is Line => line !== null);
  }, [lines, debouncedSearchText, fieldColumns, t, fieldByKeys]);

  // Memoize pivot calculations
  const pivotIndex = useMemo(() => fieldColumns.findIndex((fieldColumn) => fieldColumn.isPivot), [fieldColumns]);
  const pivotColumnNb = useMemo(() => fieldColumns.filter((fieldColumn) => fieldColumn.isPivot).length, [fieldColumns]);
  const pivotValuesNb = useMemo(() =>
    (selectedView?.pivot?.valuePathKeys?.length ?? 0) ?? 0,
    [selectedView?.pivot?.valuePathKeys]
  );
  const pivotField: Field | null = useMemo(() =>
    (selectedView?.pivot?.pivotPathKey) ? fieldByPathKeys.get(selectedView?.pivot?.pivotPathKey) || null : null,
    [selectedView?.pivot?.pivotPathKey, fieldByPathKeys]
  );
  const viewAggregations = selectedView?.aggregations
  const [timer, setTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(null);
  const [draggedOverColumnIndex, setDraggedOverColumnIndex] = useState<number | null>(null);
  const updateFieldColumns = useDataStore(state => state.updateFieldColumns);
  const updateSelectedObjectInfos = useModelStore(state => state.updateSelectedObjectInfos);
  const updateSelectedView = useModelStore(state => state.updateSelectedView);
  const RESIZE_VALIDATION_DELAY = 1000;


  const initPinnedColumns = useCallback(() => {
    if (selectedView) {
      let pinnedColumns = [];
      if (groups && groups.length > 0) {
        for (let i = 1; i < groups.length; i++) {
          pinnedColumns.push("group-" + i);
        }
      }
      pinnedColumns.push("select");
      return pinnedColumns;
    } else {
      return ["select"];
    }
  }, [selectedView?._key, groups]);

  useEffect(() => {
    if (tableContainerRef.current) {
      setTableWidth(tableContainerRef.current.clientWidth);
    }
  }, [tableContainerRef.current?.clientWidth]);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: initPinnedColumns(),
    right: [],
  });

  const getColSizingLocalStorageKey = useCallback(() => {
    return viewColumnSizingStorageId(selectedObjectInfos?.object?._key, selectedView?._key);
  }, [selectedView, selectedObjectInfos]);

  const pathKeys = useMemo(() =>
    getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, false),
    [selectedObjectInfos, fields, relationByKeys, fieldByKeys]
  );

  const colSizing = useMemo(() =>
    toTableColumnSizing(colSizes, fieldColumns, pathKeys),
    [colSizes, fieldColumns, pathKeys]
  );

  useEffect(() => {
    const stickyPathKey = selectedView?.stickyPathKey;
    let newLeftPinned: string[];
    if (stickyPathKey) {
      const stickyIndex = fieldColumns.findIndex((_, index) => pathKeys[index] === stickyPathKey);
      if (stickyIndex !== -1) {
        newLeftPinned = initPinnedColumns();
        for (let i = 0; i <= stickyIndex; i++) {
          newLeftPinned.push(columnId(pathKeys, i));
        }
      } else {
        newLeftPinned = initPinnedColumns();
      }
    } else {
      newLeftPinned = initPinnedColumns();
    }
    setColumnPinning((prev) => {
      if (prev.left?.length === newLeftPinned.length && prev.left.every((col, i) => col === newLeftPinned[i])) {
        return prev;
      }
      return { left: newLeftPinned, right: [] };
    });
  }, [selectedView?.stickyPathKey, fieldColumns]);

  const createColumns = function (fieldColumns: FieldColumn[]): ColumnDef<Line, Field>[] {

    const columnHelper = createColumnHelper<Line>();

    const columns: ColumnDef<Line, Field>[] = [];

    if (groups && groups.length > 1) {
      for (let i = 1; i < groups.length; i++) {
        columns.push(
          {
            id: "group-" + i,
            header: () => ShiftHeader({ shift: i }),
            cell: () => ShiftCell({ shift: i }),
            enableSorting: false,
            enableHiding: false,
            enableResizing: false,
            enablePinning: true,
            size: 10,
          }
        );
      }
    }

    let selectSize = SELECT_COLUMN_WIDTH;
    let selectShift = 0;
    if (groups?.length && groups.length > 0) {
      selectShift = groups.length;
    }
    columns.push({
      id: "select",
      header: ({ table, header }) => SelectHeader({ table, fieldColumn: null, index: 0, header: header, shift: selectShift }),
      cell: ({ row, table }) => SelectCell({ row, table, lineDepthRef, fieldColumn: null, index: 0 }),
      enableSorting: false,
      enableHiding: false,
      enableResizing: false,
      enablePinning: true,
      size: selectSize,
    });
    const fieldCols: ColumnDef<Line, Field>[] = [];
    fieldColumns.forEach((fieldColumn, index) => {
      const minSize = getColumnMinSize(fieldColumn);
      if (pivotIndex === -1 || (index < pivotIndex || index >= pivotIndex + pivotColumnNb)) {
        columns.push(
          columnHelper.accessor(row => row.items[index].value, {
            id: columnId(pathKeys, index),
            header: ({ table, header }) => FieldHeader({ table: table, fieldColumn: fieldColumn, index: index + 1, header: header, hasSeveralPivotValues: false }),
            cell: ({ row }) => FieldCell({ row, fieldColumn, index: index }),
            enableResizing: true,
            enableHiding: true,
            enableSorting: true,
            enableGrouping: true,
            enablePinning: true,
            minSize: minSize,
          })
        );
      } else {
        fieldCols.push(
          columnHelper.accessor(row => row.items[index].value, {
            id: columnId(pathKeys, index),
            header: ({ table, header }) => FieldHeader({ table: table, fieldColumn: fieldColumn, index: index + 1, header: header, hasSeveralPivotValues: pivotValuesNb > 1 }),
            cell: ({ row }) => FieldCell({ row, fieldColumn, index: index }),
            enableResizing: true,
            enableHiding: true,
            enableSorting: true,
            enableGrouping: true,
            enablePinning: true,
            minSize: minSize,
          })
        );

        // If the current field is the last pivot field, then fill all the pivot fields in the same group
        if (index == pivotIndex + pivotColumnNb - 1) {
          const pivotFormat = selectedView?.formats?.get(selectedView?.pivot?.pivotPathKey ?? "") ?? pivotField?.format;
          if (pivotValuesNb <= 1) {
            columns.push(
              columnHelper.group({
                id: "pivot-header",
                header: () => PivotHeader({ pivotField: pivotField, pivotIndex: pivotIndex, groupIndex: 0, pivotValue: null, pivotFormat: pivotFormat }),
                columns: fieldCols,
                enablePinning: false,
              })
            );
          } else {

            const numGroups = Math.ceil(pivotColumnNb / pivotValuesNb);
            for (let groupIndex = 0; groupIndex < numGroups; groupIndex++) {
              const start = groupIndex * pivotValuesNb;
              const end = start + pivotValuesNb;
              const subColumns = fieldCols.slice(start, end);

              // Explicit capture with const
              const capturedGroupIndex = groupIndex;
              const capturedStart = start;
              columns.push(
                columnHelper.group({
                  id: "pivot-header-" + capturedGroupIndex,
                  header: () => {
                    return PivotHeader({ pivotField: pivotField, pivotIndex: pivotIndex, groupIndex: capturedGroupIndex, pivotValue: fieldColumns[pivotIndex + capturedStart].pivotValue, pivotFormat: pivotFormat });
                  },
                  columns: subColumns,
                  enablePinning: false,
                })
              );
            }
          }
        }
      }
    });

    return columns;
  }

  const isError = useMemo(() => {
    return fieldColumns.some((fieldColumn) => !isFieldValid(fieldColumn.field, selectedObjectInfos!.object._key, relationByKeys, fieldByKeys));
  }, [fieldColumns, selectedObjectInfos, relationByKeys, fieldByKeys]);


  const columns = useMemo(() => {
    return (fieldColumns && fieldColumns.length > 0) ? createColumns(fieldColumns) : [];
  }, [fieldColumns, selectedView]);

  const resizeColumns = useCallback((updaterOrValue: Updater<ColumnSizingState>) => {

    const newColSizing: ColumnSizingState = typeof updaterOrValue === 'function' ? updaterOrValue(colSizing) : updaterOrValue;
    // Fold the change back into the stable sizes (a pivot column resize
    // applies to all the columns of the pivot)
    const newColSizes = applyTableColumnSizing(colSizes, colSizing, newColSizing, fieldColumns, pathKeys);
    setColSizes(newColSizes);

    clearTimeout(timer);
    const newTimer = setTimeout(() => {
      localStorage.setItem(getColSizingLocalStorageKey(), JSON.stringify(newColSizes));
    }, RESIZE_VALIDATION_DELAY);

    setTimer(newTimer);
  }, [getColSizingLocalStorageKey, colSizes, colSizing, fieldColumns, pathKeys]);


  const onRowSelectionChange = useCallback((updaterOrValue: Updater<RowSelectionState>) => {
    let newRowSelection: RowSelectionState;
    if (typeof updaterOrValue === 'function') {
      newRowSelection = updaterOrValue(rowSelection);
    } else {
      newRowSelection = updaterOrValue;
    }

    updateStatuses(newRowSelection, useDataStore.getState().focusPos ?? []);
    setRowSelection(newRowSelection);
  }, [rowSelection]);

  const onExpandedChange = useCallback((updaterOrValue: Updater<ExpandedState>) => {
    let newExpandedState: ExpandedState;
    if (typeof updaterOrValue === 'function') {
      newExpandedState = updaterOrValue(expandedState);
    } else {
      newExpandedState = updaterOrValue;
    }
    setExpandedState(newExpandedState);
  }, [expandedState]);

  // Function to convert a position to a string key
  const positionToKey = useCallback((pos: CellPosition): string => {
    return `${pos.rowId}-${pos.column}`;
  }, []);

  // Function to get all available rowIds in table order
  const getAllRowIds = useCallback((tableRows: Row<Line>[]): string[] => {
    const rowIds: string[] = [];
    const collectRowIds = (rows: Row<Line>[]) => {
      rows.forEach(row => {
        const rowId = row.id;
        if (rowId && !rowIds.includes(rowId)) {
          rowIds.push(rowId);
        }
        if (row.subRows && row.subRows.length > 0) {
          collectRowIds(row.subRows);
        }
      });
    };
    collectRowIds(tableRows);
    return rowIds;
  }, []);

  // Function to calculate rectangular selection between two cells
  const calculateRectangularSelection = useCallback((start: CellPosition, end: CellPosition, tableRows: Row<Line>[], metaLines: Map<string, MetaLine>): CellPosition[] => {

    const rowIds = getAllRowIds(tableRows);
    const startRowIndex = rowIds.indexOf(start.rowId);
    const endRowIndex = rowIds.indexOf(end.rowId);

    if (startRowIndex === -1 || endRowIndex === -1) {
      return [start];
    }

    const minRowIndex = Math.min(startRowIndex, endRowIndex);
    const maxRowIndex = Math.max(startRowIndex, endRowIndex);
    const minCol = Math.min(start.column, end.column);
    const maxCol = Math.max(start.column, end.column);

    const selected: CellPosition[] = [];

    const hasLeftGroup = groups?.length > 0 && groups[groups.length - 1]?.displayType === LEFT;
    for (let rowIndex = minRowIndex; rowIndex <= maxRowIndex; rowIndex++) {
      for (let col = minCol; col <= maxCol; col++) {
        const rowId = rowIds[rowIndex]
        const metaLine = metaLines.get(rowId);
        if (metaLine && metaLine.level === (groups?.length ?? 0)) {

          // Skip left group cells if the column is 0 and the rowId does not end with ".0"
          // That is we consider only the first cell of the left group
          if (hasLeftGroup && col === 0 && !rowId.endsWith(".0")) {
            continue;
          }

          selected.push({ rowId: rowId, column: col });
        }
      }
    }
    return selected;
  }, [getAllRowIds]);

  const getRowId = useCallback((originalRow: Line, index: number, parent?: Row<Line>) => {
    // Use stable _rowId if available (assigned at fetch time)
    // This ensures rowId doesn't change with frontend filtering
    return originalRow._rowId ?? ((parent ? parent.id + "." : "") + index);
  }, []);

  const table = useReactTable<Line>({
    data: filteredLines,
    columns: columns,
    enableColumnResizing: true,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableExpanding: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: MIN_COLUMN_WIDTH,
    },
    getRowId: getRowId,
    getCoreRowModel: getCoreRowModel(),
    // getExpandedRowModel: getExpandedRowModel(),
    onColumnSizingChange: resizeColumns,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: onRowSelectionChange,
    onExpandedChange: onExpandedChange,
    getRowCanExpand: (row) => { return true; },
    state: {
      columnSizing: colSizing,
      columnVisibility: columnVisibility,
      rowSelection: rowSelection,
      expanded: expandedState,
      columnPinning: columnPinning,
    },
    getSubRows: (originalRow) => originalRow.children || undefined,
    onColumnPinningChange: setColumnPinning,
  })

  // Table ref to avoid re-rendering the table when the data changes
  const tableRef = useRef(table);
  tableRef.current = table;

  const fieldColumnStarts = useMemo(() => {
    const starts: number[] = [];
    let pos = SELECT_COLUMN_WIDTH;
    table.getVisibleLeafColumns()
      .filter(col => col.id !== 'select' && !col.id.startsWith('group-'))
      .forEach(col => {
        starts.push(pos);
        pos += col.getSize();
      });
    return starts;
  }, [colSizing]);

  // Central focus store: one Zustand subscriber dispatches to only the affected cells
  const cellFocusStore = useMemo(() => createCellFocusStore(), []);

  useEffect(() => {
    const computeParams = (state: { focusPos: CellPosition[] | null; editingPos: CellPosition | null; selectionAnchor: CellPosition | null; metaLines: Map<string, any> }): CellFocusNotifyParams => {
      const { focusPos, editingPos, selectionAnchor, metaLines } = state;
      const rowIds = Array.from(metaLines.keys());
      const focusKeys = new Set(focusPos?.map(positionToKey) ?? []);
      const editingKey = editingPos ? positionToKey(editingPos) : null;

      let handleKey: string | null = null;
      let handleCorner: HandleCorner = "bottom-right";
      let focusDOMKey: string | null = null;

      if (focusPos?.length) {
        const handleCell = focusPos.length === 1 || !selectionAnchor
          ? focusPos[0]
          : getLastSelectionCell(focusPos, selectionAnchor, rowIds);

        if (handleCell) {
          handleKey = positionToKey(handleCell);
          if (focusPos.length > 1 && selectionAnchor) {
            const aRow = rowIds.indexOf(selectionAnchor.rowId);
            const hRow = rowIds.indexOf(handleCell.rowId);
            const isTop = aRow !== -1 && hRow !== -1 && hRow < aRow;
            const isLeft = handleCell.column < selectionAnchor.column;
            handleCorner = `${isTop ? "top" : "bottom"}-${isLeft ? "left" : "right"}` as HandleCorner;
          }
        }

        if (!editingPos) {
          if (focusPos.length === 1) {
            focusDOMKey = positionToKey(focusPos[0]);
          } else if (selectionAnchor) {
            const last = getLastSelectionCell(focusPos, selectionAnchor, rowIds);
            if (last) focusDOMKey = positionToKey(last);
          } else {
            focusDOMKey = positionToKey(focusPos[focusPos.length - 1]);
          }
        }
      }

      return { focusKeys, editingKey, handleKey, handleCorner, focusDOMKey };
    };

    // The browser does not know about the frozen columns, and it centers a
    // hidden element it focuses: once a cell has taken the focus (click, arrow
    // keys...), its horizontal scroll is replaced by the smallest one showing
    // the cell right of the frozen columns
    let scrollLeftBeforeFocus = 0;
    const domFocusHooks = {
      beforeMove: () => {
        scrollLeftBeforeFocus = scrollerRef.current?.scrollLeft ?? 0;
      },
      afterMove: () => {
        const scroller = scrollerRef.current;
        if (scroller) {
          scroller.scrollLeft = scrollLeftBeforeFocus;
          revealGridCell(scroller, document.activeElement);
        }
      },
    };

    return syncCellFocus(cellFocusStore, useDataStore, computeParams, domFocusHooks);
  }, [cellFocusStore]);

  const updateStatuses = useCallback((rowSelection: RowSelectionState, selectedCells: CellPosition[]) => {

    const statuses: Status[] = []
    if (Object.keys(rowSelection).length > 0) {
      statuses.push({ label: t('data.table.status.selected.rows'), value: Object.keys(rowSelection).length } as Status);
    }

    const numericValues: Array<number> = [];
    selectedCells.forEach((position) => {
      const row = tableRef.current.getRowModel().rowsById[position.rowId];
      if (row) {
        const line = row.original as Line;
        const item = line.items[position.column];
        if (item && typeof item.value === 'number') {
          numericValues.push(item.value);
        }
      }
    });

    const sum = numericValues.reduce((acc, value) => acc + value, 0);
    const average = numericValues.length > 0 ? sum / numericValues.length : 0;
    const max = numericValues.length > 0 ? Math.max(...numericValues) : 0;
    const min = numericValues.length > 0 ? Math.min(...numericValues) : 0;
    if (selectedCells.length > 1) {
      statuses.push({ label: t('data.table.status.count'), value: selectedCells.length } as Status);
    }
    if (numericValues.length > 1) {
      statuses.push({ label: t('data.table.status.sum'), value: sum.toFixed(2) } as Status);
      statuses.push({ label: t('data.table.status.average'), value: average.toFixed(2) } as Status);
      statuses.push({ label: t('data.table.status.max'), value: max.toFixed(2) } as Status);
      statuses.push({ label: t('data.table.status.min'), value: min.toFixed(2) } as Status);
    }
    onStatusesChange(statuses);
  }, [table]);

  // Function to handle selection changes (must be after table creation)
  const handleSelectionChange = useCallback((clickedPosition: CellPosition, shiftKey: boolean, ctrlKey: boolean) => {
    const tableRows = table.getRowModel().rows;
    let newSelectedCells: Set<string>;
    let newSelectionAnchor: CellPosition | null;

    // Use store to get the most recent values
    const currentAnchor = useDataStore.getState().selectionAnchor;
    // const currentSelectedCells = selectedCellsRef.current;
    const currentFocusPos = useDataStore.getState().focusPos;
    const metaLines = useDataStore.getState().metaLines;
    const currentSelectedCells = new Set<string>(currentFocusPos?.map(pos => positionToKey(pos)) ?? []);

    if (shiftKey) {
      // Rectangular selection
      let anchor = currentAnchor;
      // If still no anchor, use the clicked cell
      if (!anchor) {
        anchor = clickedPosition;
      }
      const rectangularSelection = calculateRectangularSelection(anchor, clickedPosition, tableRows, metaLines);
      newSelectedCells = new Set(rectangularSelection.map(pos => positionToKey(pos)));
      focusMultiple(rectangularSelection);

    } else if (ctrlKey) {
      // Add/remove from selection (without modifying anchor for rectangular selection)
      const clickedKey = positionToKey(clickedPosition);
      newSelectedCells = new Set(currentSelectedCells);
      if (newSelectedCells.has(clickedKey)) {
        newSelectedCells.delete(clickedKey);
      } else {
        newSelectedCells.add(clickedKey);
      }
      // If removing the last cell, reset the anchor
      if (newSelectedCells.size === 0) {
        newSelectionAnchor = null;
      } else {
        newSelectionAnchor = currentAnchor; // Keep existing anchor to allow Shift+click after
      }
      // Update state BEFORE focusMultiple to ensure isSelected is correct when cells re-render
      setSelectionAnchor(newSelectionAnchor);

      focusMultiple(newSelectedCells.size > 0 ? Array.from(newSelectedCells).map(key => {
        const [rowId, column] = key.split('-');
        return { rowId, column: parseInt(column) };
      }) : null);
    } else {
      // Simple selection: replace selection and reset anchor
      newSelectedCells = new Set([positionToKey(clickedPosition)]);
      newSelectionAnchor = clickedPosition;

      // Update state BEFORE focus to ensure isSelected is correct when cells re-render
      setSelectionAnchor(newSelectionAnchor);

      focus(clickedPosition);
    }

    // Convert to CellPosition array and call onSelectionChange
    const selectedPositions: CellPosition[] = Array.from(newSelectedCells).map(key => {
      const [rowId, column] = key.split('-');
      return { rowId, column: parseInt(column) };
    });

    // Call onSelectionChange function if it exists
    // if (onSelectionChange) {
    //   onSelectionChange(selectedPositions);
    // }
    const selectedRows = useDataStore.getState().rowSelection;
    updateStatuses(selectedRows, selectedPositions);
  }, [calculateRectangularSelection, positionToKey, updateStatuses, table, focus, setSelectionAnchor]);

  const startSelectionHandleDrag = useCallback((position: CellPosition) => {
    isSelectionHandleDraggingRef.current = true;
    handleSelectionChange(position, true, false);
  }, [handleSelectionChange]);

  const extendSelectionHandleDrag = useCallback((position: CellPosition) => {
    if (!isSelectionHandleDraggingRef.current) {
      return;
    }
    handleSelectionChange(position, true, false);
  }, [handleSelectionChange]);

  const closeContextMenu = useCallback(() => {
    contextMenuPasteActionRef.current = null;
    contextMenuDeleteActionRef.current = null;
    setContextMenu((prev) => prev.open ? { ...prev, open: false } : prev);
    cellFocusStore.triggerRefocus();
  }, [cellFocusStore]);

  const openContextMenu = useCallback((x: number, y: number, canPaste: boolean, pasteAction: () => void, canDelete: boolean, deleteAction: () => void) => {
    contextMenuPasteActionRef.current = pasteAction;
    contextMenuDeleteActionRef.current = deleteAction;
    setContextMenu({
      open: true,
      x,
      y,
      canPaste,
      canDelete,
    });
  }, []);

  useEffect(() => {
    const stopSelectionHandleDrag = () => {
      isSelectionHandleDraggingRef.current = false;
    };

    window.addEventListener("mouseup", stopSelectionHandleDrag);
    window.addEventListener("blur", stopSelectionHandleDrag);

    return () => {
      window.removeEventListener("mouseup", stopSelectionHandleDrag);
      window.removeEventListener("blur", stopSelectionHandleDrag);
    };
  }, []);

  useEffect(() => {
    if (!contextMenu.open) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (contextMenuRef.current?.contains(event.target as Node)) {
        return;
      }
      closeContextMenu();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeContextMenu();
      }
    };

    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", closeContextMenu, true);
    window.addEventListener("blur", closeContextMenu);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", closeContextMenu, true);
      window.removeEventListener("blur", closeContextMenu);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu.open, closeContextMenu]);

  // Basic virtualization setup
  const { rows } = table.getRowModel();
  const activeSelectionHandleCell = useMemo(() => {
    if (!focusPos?.length) {
      return null;
    }
    if (focusPos.length === 1 || !selectionAnchor) {
      return focusPos[0] || null;
    }
    return getLastSelectionCell(focusPos, selectionAnchor, getAllRowIds(rows));
  }, [focusPos, selectionAnchor, rows, getAllRowIds]);
  const lineDepth = useMemo(() => {
    return groups?.length ?? 0;
  }, [groups]);
  const view = useModelStore(state => state.selectedView);
  const { height } = useWindowDimensions();
  const wsColor = useModelStore(state => state.wsColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  // Check if virtualization should be enabled (disable for grouped data)
  // const shouldUseVirtualization = lineDepth === 0 ||
  //   (groups?.length === 1 && groups[0].displayType === LEFT && !rows.some((row) => row.subRows && row.subRows.length > 1)); // Only use virtualization when no grouping

  // const shouldUseVirtualization = useMemo(() => lineDepth <= 1, [lineDepth]);
  const shouldUseVirtualization = true;

  // Optimized virtualization state with memoized calculations
  const [scrollTop, setScrollTop] = useState(0);
  const rowHeight = LINE_ROW_HEIGHT;

  // Memoize virtualization calculations
  const visibleCount = useMemo(() => Math.ceil(height / rowHeight), [height, rowHeight]);
  const rowLength = useMemo(() => {
    if (groups?.length === 1 && groups[0].displayType === LEFT) {
      return rows.reduce((acc, row) => acc + row.subRows.length, 0);
    } else {
      return rows.length;
    }
  }, [rows, groups]);

  const onEditionStart = useCallback(() => {
    setIsAddingRecord(true);
  }, []);

  const onEditionEnd = useCallback(() => {
    setIsAddingRecord(false);
  }, []);

  const getRowHeight = useCallback((row: Row<Line>, level: number): number => {
    // Case of a group
    if (level < lineDepth) {
      // Case of a horizontal group
      if (level === lineDepth - 1 && groups[level].displayType === LEFT) {
        // In case of a horizontal group with no children, the group height is the sum of children rows height (or 1 row height if no children)
        return (row.subRows?.length ?? 1) * LINE_ROW_HEIGHT;
      }

      // Case of a vertical group
      else {
        const addButtonRowHeight = 42;
        // If the group is expanded, add the children rows height and the add button height to the group row height
        if (row.getIsExpanded()) {
          const childrenRowHeight = row.subRows?.reduce((acc: number, child: Row<Line>) => acc + getRowHeight(child, level + 1), 0) ?? 0;
          return GROUP_ROW_HEIGHT + childrenRowHeight + addButtonRowHeight;
        }
        // If the group is not expanded, consider only the group row height
        else {
          return GROUP_ROW_HEIGHT;
        }
      }
    }

    // Case of a leaf line
    else {
      // Return the line row height
      return LINE_ROW_HEIGHT;
    }
  }, [lineDepth, groups, expandedState]);

  const startIndex = useMemo(() => {
    if (shouldUseVirtualization) {

      const rowMargin = 5;

      // No groups, use the default calculation
      if (!groups.length || groups?.length === 0) {
        return Math.max(0, Math.floor(scrollTop / rowHeight) - rowMargin);
      }

      // There is one group, use the group row height and the children rows height
      let result = 0;
      let index = 0;
      while (result < scrollTop && index < rows.length) {
        result += getRowHeight(rows[index], 0);
        index++;
      }

      return Math.max(0, index - rowMargin);
    } else {
      return 0;
    }
  }, [shouldUseVirtualization, scrollTop, groups, rows, expandedState]);

  // const startIndex = useMemo(() => 
  //   shouldUseVirtualization ? Math.max(0, Math.floor(scrollTop / rowHeight) - 5) : 0,
  //   [shouldUseVirtualization, scrollTop, rowHeight]
  // );

  // const endIndex = useMemo(() => 
  //   shouldUseVirtualization ? Math.min(rowLength, startIndex + visibleCount + 15) : rowLength,
  //   [shouldUseVirtualization, rowLength, startIndex, visibleCount]
  // );

  const endIndex = useMemo(() => {
    if (shouldUseVirtualization) {

      const rowMargin = 15;

      // No groups, use the default calculation
      if (!groups.length || groups?.length === 0) {
        return Math.min(rowLength, startIndex + visibleCount + rowMargin);
      }

      // There is one group, use the group row height and the children rows height
      let result = 0;
      let index = 0;
      while (result < scrollTop + height && index < rows.length) {
        result += getRowHeight(rows[index], 0);
        index++;
      }

      return Math.min(rowLength, index + 1 + rowMargin)
    } else {
      return rowLength;
    }
  }, [shouldUseVirtualization, scrollTop, groups, rows, expandedState]);


  // Use ref to store scroll position to avoid stale closures
  const scrollTopRef = useRef(0);
  const scrollTimeoutRef = useRef<number>();
  const followGridScrollLeft = useScrollLeftKeptAcrossClear(scrollerRef, fieldColumns.length > 0);

  useLayoutEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
    setScrollTop(0);
  }, [scrollTopResetKey]);

  // Optimized scroll handler using requestAnimationFrame for better performance
  const handleScroll = useCallback((e: React.UIEvent<HTMLTableElement>) => {
    const newScrollTop = e.currentTarget.scrollTop;
    scrollTopRef.current = newScrollTop;
    followGridScrollLeft(e.currentTarget);

    // Use requestAnimationFrame for smoother performance
    if (scrollTimeoutRef.current) {
      cancelAnimationFrame(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = requestAnimationFrame(() => {
      if (Math.abs(newScrollTop - scrollTop) > 5) {
        setScrollTop(newScrollTop);
      }
    });
  }, [scrollTop]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        cancelAnimationFrame(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const storedSizes = localStorage.getItem(getColSizingLocalStorageKey());
    if (storedSizes) {
      setColSizes(JSON.parse(storedSizes));
    }
  }, []);

  // Widths persisted by earlier versions are keyed by positional column id:
  // convert them to stable keys once the columns are known
  useEffect(() => {
    if (fieldColumns.length === 0) {
      return;
    }
    const { migrated, sizes } = migrateLegacyColumnSizing(colSizes, fieldColumns, pathKeys);
    if (migrated) {
      setColSizes(sizes);
      localStorage.setItem(getColSizingLocalStorageKey(), JSON.stringify(sizes));
    }
  }, [colSizes, fieldColumns, pathKeys, getColSizingLocalStorageKey]);

  // useEffect(() => {
  //   // Columns to hide are columns marked as hidden and also in a group
  //   const columnsToHide = hiddenFields.filter(field => groupPaths.includes(field));
  //   const columnVisibility = hideColunms(columnsToHide);
  //   table.setColumnVisibility(columnVisibility);
  // }, [hiddenFields, groupPaths]);

  interface PivotHearderProps {
    pivotField: Field | null;
    pivotIndex: number;
    groupIndex: number;
    pivotValue?: Item | null;
    pivotFormat?: Map<string, any> | undefined;
  }

  function PivotHeader({ pivotField, pivotIndex, groupIndex, pivotValue, pivotFormat }: PivotHearderProps) {
    const [pivotHovered, setPivotHovered] = useState(false);
    const onMouseOver = () => {
      setPivotHovered(true);
    }
    const onMouseOut = () => {
      setPivotHovered(false);
    }
    const positionStyle = {
      position: "inherit",
      // right: "0.5rem",
    } as React.CSSProperties;

    let pivotFieldAggr: AggregationType | undefined
    const pivotPathKey = selectedView?.pivot?.pivotPathKey;
    if (pivotPathKey) {
      pivotFieldAggr = viewAggregations?.get(pivotPathKey)
    }
    if (!pivotFieldAggr) {
      pivotFieldAggr = pivotField?.aggregation
    }

    let value = pivotField?.name || "";
    if (pivotValue?.value && pivotField) {
      value = itemToString(pivotValue, pivotField, pivotFieldAggr, pivotFormat, t, t('data.table.emptyGroup'), fieldByKeys);
    }
    return (
      <div key={"grid-header-cell-pivot-" + pivotField?._key + "-" + pivotIndex + "-" + groupIndex} className={TW_DataTableGridHeaderCell} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
        <p className="text-center w-fit max-w-full min-w-0 line-clamp-2 break-words">{value}</p>
        {pivotField &&
          <ViewFieldMenuButton key={"grid-header-cell-menu-" + pivotField?._key} field={pivotField} index={pivotIndex} visible={pivotHovered} positionStyle={positionStyle} stickable={false} hiddable={false} />
        }
      </div>
    )
  }

  function FieldHeader({ fieldColumn, index, header, hasSeveralPivotValues }: CellHeaderProps & { hasSeveralPivotValues: boolean }) {
    const [isHovered, setIsHovered] = useState(false);

    const onMouseOver = () => {
      setIsHovered(true);
    }
    const onMouseOut = () => {
      setIsHovered(false);
    }
    let isToday = false;

    // Get workspace color for today's date highlighting
    const wsColor = useModelStore((state) => state.wsColor);
    const isDarkColor = useModelStore((state) => state.wsIsDarkColor);

    if (!fieldColumn) {
      return null;
    }
    if (!header) {
      return null;
    }
    const field = fieldColumn.srcField || fieldColumn.field;
    const isPivot = fieldColumn.isPivot;
    let value = field.name;
    let headerType: FieldType | undefined = field.type;

    let pivotFieldAggr: AggregationType | undefined
    const pivotPathKey = selectedView?.pivot?.pivotPathKey;
    if (pivotPathKey) {
      pivotFieldAggr = viewAggregations?.get(pivotPathKey)
    }
    if (!pivotFieldAggr) {
      pivotFieldAggr = pivotField?.aggregation
    }
    if (isPivot && pivotField && !hasSeveralPivotValues) {
      headerType = pivotField.type;
      const format = selectedView?.formats?.get(pivotPathKey ?? "") ?? pivotField?.format;
      if (pivotField.type === FieldType.Date) {
        value = valueToString(fieldColumn.pivotValue?.value, pivotField, pivotFieldAggr, format, t, t('data.table.emptyGroup'), fieldByKeys);

        // Check if this pivot value represents today's date
        if (fieldColumn.pivotValue?.value) {
          const today = new Date();


          if (pivotFieldAggr && (pivotFieldAggr === AggregationType.WEEK || pivotFieldAggr === AggregationType.MONTH || pivotFieldAggr === AggregationType.QUARTER || pivotFieldAggr === AggregationType.YEAR)) {
            const period = Period.fromObject(fieldColumn.pivotValue.value);
            if (period) {
              if (period.isInPeriod(today)) {
                isToday = true;
              }
            }
          } else {
            const todayString = today.toISOString().substring(0, 10);
            const pivotValueString = fieldColumn.pivotValue.value.toString();
            // Compare the date strings (both should be in YYYY-MM-DD format)
            if (pivotValueString === todayString) {
              isToday = true;
            }
          }
        }
      } else if (pivotField.type === FieldType.Period) {
        value = valueToString(fieldColumn.pivotValue?.value, pivotField, pivotFieldAggr, format, t, t('data.table.emptyGroup'), fieldByKeys);

        // Check if this pivot value represents a period that contains today's date
        if (fieldColumn.pivotValue?.value) {
          try {
            const period = Period.fromObject(fieldColumn.pivotValue.value);
            if (period) {
              const today = new Date();
              if (period.isInPeriod(today)) {
                isToday = true;
              }
            }
          } catch (error) {
            console.warn('Error parsing period for today check:', error);
          }
        }
      } else if (typeof fieldColumn.pivotValue?.value === "string") {
        value = fieldColumn.pivotValue?.value;
      } else {
        value = itemToString(fieldColumn.pivotValue?.value as Item, pivotField, pivotFieldAggr, format, t, t('data.table.emptyGroup'), fieldByKeys);
      }
    }

    // Apply workspace color background if this is today's date
    const headerStyle = isToday ? {
      backgroundColor: wsColor,
      color: isDarkColor ? "white" : ""
    } : {};


    let tooltipContent: string | React.ReactNode = field.name;
    if (isPivot && pivotField) {
      tooltipContent = value;
    } else if (isColumnInError(fieldColumn)) {
      tooltipContent = columnErrorMessage(t, fieldColumn);
    } else if (field.description && field.description.trim() !== "") {
      tooltipContent = textToHTML(field.description);
    }

    // Compute text width based on font properties
    const fontSize = '14px'; // Default Tailwind text size - text-sm
    const fontFamily = 'Kablammo, sans-serif'; // Default Tailwind font stack
    const fontWeight = 'normal';
    const horizontalPadding = isToday ? 16 : 0; // px-2 = 0.5rem * 2 sides = 16px when isToday
    const computedWidth = computeTextWidth(value, fontSize, fontFamily, fontWeight, horizontalPadding);

    // Get actual column size from TanStack Table
    const actualColumnSize = header.getSize();

    // Show tooltip if field has a description
    const fieldNameWithTooltip = (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger className="min-w-0 overflow-hidden" style={headerStyle}>
            <span className={TW_DataTableGridHeaderCellText + (isPivot ? " text-center" : "") + (isToday ? " w-fit max-w-full rounded-md px-1 py-1" : "")} style={headerStyle}>{value}</span>
          </TooltipTrigger>
          <TooltipContent>
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )

    // A column the client cannot resolve, or the server could not serve
    // (deleted field or relation, formula depending on one), shows the
    // warning icon; the lines of the other columns are still displayed.
    const isError = !isFieldValid(field, selectedObjectInfos!.object._key, relationByKeys, fieldByKeys) || isColumnInError(fieldColumn);
    if (isError) {
      headerType = undefined;
    }


    const hideMargin = 16 + 16 + 6; // 16px for type icon, 16 px for menu icon, 6 px for margin
    const showIcons = !selectedView && (computedWidth + hideMargin < actualColumnSize);

    const positionStyle = {
      position: "absolute",
      right: "0.5rem",
      minWidth: "1px",
      minHeight: "1px",
      width: "1px",
    } as React.CSSProperties;

    return (
      <div key={"grid-header-cell-" + field._key + "-" + index} className={TW_DataTableGridHeaderCell + (selectedView === null ? " gap-2" : "")} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
        {showIcons && React.createElement(fieldTypeIcon(headerType), { className: TW_DataTableGridHeaderCellIcon + (isError ? " text-red-500" : "") })}
        {fieldNameWithTooltip}
        {selectedView === null &&
          <ConfigFieldMenuButton key={"grid-header-cell-menu-" + field._key + "-" + index} field={field} isCellHovered={isHovered} visible={showIcons} />
        }
        {selectedView !== null &&
          <ViewFieldMenuButton key={"grid-header-cell-menu-" + field._key + "-" + index} field={field} index={index} visible={isHovered} positionStyle={positionStyle} stickable={!isPivot} hiddable={true} />
        }
      </div>
    );
  }

  function FieldCell({ row, fieldColumn, index }: CellProps) {
    const position: CellPosition = useMemo(() => { return { rowId: row.id, column: index } }, [row.id, index]);
    const line = useMemo(() => row.original as Line, [row.original]);
    const item = useMemo(() => line.items[index], [line.items, index]);
    // const positionKey = useMemo(() => positionToKey(position), [position, positionToKey]);
    // const isSelected = useMemo(() => selectedCells.has(positionKey), [selectedCells, positionKey]);

    const field = fieldColumn?.srcField || fieldColumn?.field;
    if (!field) {
      return null;
    }

    if (item) {
      return <GridCell
        key={"grid-cell-" + item.key.fieldKey + "-" + item.key.recordKey + "-" + index}
        fieldColumn={fieldColumn}
        item={item}
        position={position}
        // isSelected={isSelected}
        onSelectionChange={handleSelectionChange}
        onSelectionHandleDragStart={startSelectionHandleDrag}
        onSelectionHandleDragOver={extendSelectionHandleDrag}
        onContextMenuOpen={openContextMenu}
      />
    }
    return <div key={"grid-cell-" + field._key + "-null"} />
  };

  const onDragStart = useCallback((e: React.DragEvent<HTMLTableHeaderCellElement>) => {
    const columnIndex = e.currentTarget.dataset.columnIndex;
    if (columnIndex) {
      const shift = groups?.length ? (groups.length - 1) : 0;
      setDraggedColumnIndex(parseInt(columnIndex) - shift);
    }
  }, [groups]);

  const onDragOver = useCallback((e: React.DragEvent<HTMLTableHeaderCellElement>) => {
    const columnIndex = e.currentTarget.dataset.columnIndex;
    if (columnIndex) {
      setDraggedOverColumnIndex(parseInt(columnIndex));
    }
  }, []);

  // While a column header is dragged, scroll the grid when the pointer gets
  // close to the frozen columns or to the right edge of the grid: the browser
  // only scrolls at the very edges of the scroll container, the left one being
  // under the frozen columns.
  const isDraggingColumn = draggedColumnIndex !== null;
  useEffect(() => {
    if (!isDraggingColumn) {
      return;
    }
    let pointer: { x: number, y: number } | null = null;
    const onDocumentDragOver = (e: DragEvent) => {
      pointer = { x: e.clientX, y: e.clientY };
    };
    const onDocumentDragLeave = (e: DragEvent) => {
      // Leaving the window: no more dragover events
      if (!e.relatedTarget) {
        pointer = null;
      }
    };
    const onDocumentDragStop = () => {
      pointer = null;
    };
    let frame = requestAnimationFrame(function step() {
      const scroller = scrollerRef.current;
      if (scroller && pointer) {
        const rect = scroller.getBoundingClientRect();
        if (pointer.y >= rect.top && pointer.y <= rect.bottom) {
          const bounds = scrollableBounds(scroller);
          const speed = dragScrollSpeed(pointer.x, bounds.left, bounds.right);
          if (speed !== 0) {
            scroller.scrollLeft += speed;
          }
        }
      }
      frame = requestAnimationFrame(step);
    });
    document.addEventListener("dragover", onDocumentDragOver);
    document.addEventListener("dragleave", onDocumentDragLeave);
    document.addEventListener("drop", onDocumentDragStop);
    document.addEventListener("dragend", onDocumentDragStop);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("dragover", onDocumentDragOver);
      document.removeEventListener("dragleave", onDocumentDragLeave);
      document.removeEventListener("drop", onDocumentDragStop);
      document.removeEventListener("dragend", onDocumentDragStop);
    };
  }, [isDraggingColumn]);

  const groupAggregations = useMemo(() => {
    return groupFields.map((groupField) => {
      const path = fieldToPath(selectedObjectInfos!.object._key, relationByKeys, fieldByKeys, groupField);
      let aggregation = groupField.aggregation;
      if (path) {
        const pKey = pathKey(path);
        aggregation = selectedView?.aggregations?.get(pKey) ?? groupField.aggregation;
      }
      return aggregation ?? AggregationType.NO_AGGR;
    });
  }, [groupFields, selectedObjectInfos?.object._key, relationByKeys, fieldByKeys, selectedView?.aggregations]);



  function moveFieldAfter(srcFieldColumn: FieldColumn, dstIndex: number) {

    let order = 0;
    if (dstIndex < 0) {
      const destOrder = getSrcField(fieldColumns[0]).order ?? 0;
      order = destOrder - DEFAULT_ORDER_INCREMENT;
    } else if (dstIndex >= fieldColumns.length - 1) {
      const destOrder = getSrcField(fieldColumns[fieldColumns.length - 1]).order ?? 0;
      order = destOrder + DEFAULT_ORDER_INCREMENT;
    } else {
      const destOrder = getSrcField(fieldColumns[dstIndex]).order ?? 0;
      const nextOrder = getSrcField(fieldColumns[dstIndex + 1]).order ?? 0;
      order = (destOrder + nextOrder) / 2;
    }

    const srcField = getSrcField(srcFieldColumn);
    const newField: Field = {
      ...srcField,
      order: order,
    }

    put<void>(`${BASE_URL}/model/workspaces/${workspace!._key}/objects/${selectedObjectInfos!.object._key}/fields`, newField).then(response => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
      } else {

        const newFieldColumns = fieldColumns.map(fieldColumn => {
          if (fieldColumn.srcField && fieldColumn.srcField._key === newField._key) {
            return { ...fieldColumn, srcField: newField };
          } else if (fieldColumn.field && fieldColumn.field._key === newField._key) {
            return { ...fieldColumn, field: newField };
          } else {
            return fieldColumn;
          }
        });
        newFieldColumns.sort((a, b) => {
          const srcFieldA = getSrcField(a);
          const srcFieldB = getSrcField(b);
          return (srcFieldA.order ?? 0) - (srcFieldB.order ?? 0);
        });

        // The column widths are keyed by path key: they follow the columns
        // through the reorder without any remapping
        updateFieldColumns(newFieldColumns);

        const newFields = newFieldColumns.map(fieldColumn => getSrcField(fieldColumn));
        const newObjectInfo: ObjectInfos = { ...selectedObjectInfos!, fields: newFields };
        updateSelectedObjectInfos(newObjectInfo, selectedView, null, sharedObjectInfos);
      }
    })
  }

  function moveFieldAfterForView(srcFieldColumn: FieldColumn, dstIndex: number) {

    if (!selectedView) {
      return;
    }

    const srcField = getSrcField(srcFieldColumn);
    const srcFieldPath = fieldToPath(selectedObjectInfos!.object._key, relationByKeys, fieldByKeys, srcField);
    if (!srcFieldPath) {
      return;
    }
    const srcFieldPathKey = pathKey(srcFieldPath);
    const currentOrder = selectedView.fieldOrder ?? [];
    for (const key of pathKeys) {
      if (key && currentOrder.indexOf(key) === -1) {
        currentOrder.push(key);
      }
    }

    let srcFieldIndex = currentOrder.indexOf(srcFieldPathKey);
    if (srcFieldIndex === -1) {
      return;
    }
    let destFieldIndex = -1;
    if (dstIndex <= -1) {
      destFieldIndex = -1;
    } else {
      const destField = getSrcField(fieldColumns[dstIndex]);
      const destFieldPath = fieldToPath(selectedObjectInfos!.object._key, relationByKeys, fieldByKeys, destField);
      if (!destFieldPath) {
        return;
      }
      // Check if the destField is the field of the last group and if this group is horizontal
      // If so, this field is not in the current order but at the first position
      const destFieldPathKey = pathKey(destFieldPath);
      if (groups && groups.length > 0 && groups[groups.length - 1].pathKey === destFieldPathKey && groups[groups.length - 1].displayType === LEFT) {
        destFieldIndex = -1;
      } else {
        destFieldIndex = currentOrder.indexOf(destFieldPathKey);
      }
    }

    if (destFieldIndex < -1 || destFieldIndex === srcFieldIndex || destFieldIndex + 1 === srcFieldIndex) {
      return;
    }

    let newOrder = [...currentOrder];
    newOrder = moveElement(newOrder, srcFieldIndex, destFieldIndex);

    const newView: View = { ...selectedView, fieldOrder: newOrder.filter((key): key is string => key !== null) };
    put<View>(`${BASE_URL}/model/workspaces/${workspace!._key}/objects/${selectedObjectInfos!.object._key}/views`, newView).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            console.error(translate(t, cError.i18n, cError.params));
          } else {
            console.error(cError.message);
          }
        }
      } else {
        updateSelectedView(selectedObjectInfos, newView, null, sharedObjectInfos);
      }
    })
  }

  const onDragEnd = useCallback((e: React.DragEvent<HTMLTableHeaderCellElement>) => {
    if (draggedColumnIndex !== null && draggedOverColumnIndex !== null) {
      const shift = groups?.length ? (groups.length - 1) : 0;
      const dragEndColumnIndex = draggedOverColumnIndex - shift;
      if (dragEndColumnIndex !== draggedColumnIndex) {
        if (selectedView === null) {
          moveFieldAfter(fieldColumns[draggedColumnIndex - 1], dragEndColumnIndex - 1);
        } else {
          moveFieldAfterForView(fieldColumns[draggedColumnIndex - 1], dragEndColumnIndex - 1);
        }
      }
    }
    // Also reset when the header was dropped without hovering another one:
    // the drag is over (and its auto-scroll with it)
    setDraggedOverColumnIndex(null);
    setDraggedColumnIndex(null);
  }, [groups, draggedColumnIndex, draggedOverColumnIndex, selectedView, fieldColumns]);

  // Memoize style calculations to prevent unnecessary re-renders
  const groupShift = useMemo(() => (groups?.length ?? 0) > 0 ? groups!.length - 1 : 0, [groups?.length]);
  const colShift = useMemo(() => groupShift + 1, [groupShift]);
  const tableDivHeight = useMemo(() => height - 104, [height]);
  const tableContentHeight = tableDivHeight - 12;

  // Width of the frozen columns (the select column included), which cover the
  // left part of the scroll container: declared as its scroll padding, the
  // browser keeps the elements it scrolls into view (focus...) out from under
  // them, and the grid reads it back (gridScroll)
  const frozenWidth = table.getLeftTotalSize();

  const tableDiv1Style = useMemo(() => ({
    maxHeight: tableDivHeight + "px",
    height: tableDivHeight + "px",
    scrollbarColor: wsColor + " " + wsLightColor,
    scrollPaddingLeft: frozenWidth + "px",
  }), [tableDivHeight, wsColor, wsLightColor, frozenWidth]);

  const tableDiv2Style = useMemo(() => ({
    maxHeight: tableContentHeight + "px",
    height: tableContentHeight + "px",
    scrollbarColor: wsColor + " " + wsLightColor,
  }), [tableContentHeight, wsColor, wsLightColor]);

  // Mirrors the ceiling the last fetch actually applied, so toggling
  // "Afficher toutes les lignes" updates the notice and the add-record button
  // right away.
  const showAllLines = useDataStore(state => state.appliedLimit === null);

  const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
  const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);

  const hasAddAccessOnObject = hasGlobalRightOption(user, userRole, ObjectRightType, AddRightOption, selectedObjectInfos?.object?._key);
  const hasAddAccessOnView = hasGlobalRightOption(user, userRole, ViewRightType, AddRightOption, selectedView?._key);

  const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
  const hasAddAccess = hasEditAccess && hasAddAccessOnObject && hasAddAccessOnView;
  const { virtualizedBeforeHeight, virtualizedAfterHeight } = useMemo(() => {
    if (!shouldUseVirtualization) return { virtualizedBeforeHeight: 0, virtualizedAfterHeight: 0 };
    let before = 0, after = 0;
    for (let i = 0; i < startIndex; i++) before += getRowHeight(rows[i], 0);
    for (let i = endIndex; i < rows.length; i++) after += getRowHeight(rows[i], 0);
    return { virtualizedBeforeHeight: before, virtualizedAfterHeight: after };
  }, [shouldUseVirtualization, startIndex, endIndex, rows, getRowHeight]);

  var addRecordButtonDisabled = !showAllLines || !hasAddAccess;
  var addRecordButtonTooltip = undefined;
  if (!hasAddAccess) {
    addRecordButtonTooltip = t("data.table.no.add.access.adding.record.disabled");
  } else if (!showAllLines) {
    addRecordButtonTooltip = t("data.table.not.all.lines.shown.adding.record.disabled");
  }

  const hasAddRecordRow = !!(fields && fields.length !== 0 && (!view?.groups?.length || view?.groups?.length === 0));

  return (
    <CellFocusContext.Provider value={cellFocusStore}>
      <SelectionContextMenu
        open={contextMenu.open}
        x={contextMenu.x}
        y={contextMenu.y}
        canPaste={contextMenu.canPaste}
        canDelete={contextMenu.canDelete}
        menuRef={contextMenuRef}
        onCopy={() => copyAll(t)}
        onPaste={() => contextMenuPasteActionRef.current?.()}
        onDelete={() => contextMenuDeleteActionRef.current?.()}
        onClose={closeContextMenu}
      />
      {loadingData && (
        <div className={TW_CompozzSpinnerContainer + " z-50 "}>
          <CompozzSpinner />
        </div>
      )}
      <div ref={scrollerRef} className="flex flex-row justify-start items-start gap-0 relative w-full h-full overflow-auto scrollbar-thin scrollbar-thumb-compozz-dark-blue scrollbar-track-compozz-light-blue scrollbar-button-hidden" onScroll={handleScroll} style={tableDiv1Style}>
        <div className={"relative w-fit h-min"} style={tableDiv2Style}>
          <Table ref={tableContainerRef} className={TW_DataTable} key={"grid-table-" + selectedObjectInfos?.object.name + "-" + selectedView?.name + "-" + pivotField?._key}>
            {/* The table has a fixed layout: without explicit column widths
                the browser would derive them from the first header row and
                spread a spanning header (pivot) evenly over its columns,
                whatever the sizes of the columns — the absolutely positioned
                group summaries follow the sizes, so keep both in sync */}
            <colgroup>
              {table.getVisibleLeafColumns().map((column) => (
                <col key={"col-" + column.id} style={{ width: column.getSize() }} />
              ))}
            </colgroup>
            <TableHeader className={TW_DataTableGridHeader}>
              {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
                <TableRow className={TW_DataTableGridHeaderTR} key={headerGroup.id}>
                  {headerGroup.headers.map((header, headerIndex) => {
                    let groupHeaderStyle = "";
                    let isEmpty = false;
                    if (pivotIndex != -1 && headerGroupIndex == 0) {
                      const nbGroups = Math.ceil(pivotColumnNb / pivotValuesNb);
                      if (pivotValuesNb <= 1 && (headerIndex !== pivotIndex + colShift)) {
                        isEmpty = true;
                      } else if (pivotValuesNb > 1) {
                        if (headerIndex < pivotIndex + colShift || headerIndex >= pivotIndex + nbGroups + colShift) {
                          isEmpty = true;
                        }
                      }
                      if (headerIndex >= pivotIndex + colShift && headerIndex < pivotIndex + nbGroups + colShift) {
                        groupHeaderStyle = "white text-center text-gray-600 border-l-1 border-r-0 border-t-1 border-b-1 border-gray-200";
                      }
                    }
                    let dragOverStyle = "";
                    let dragOverInlineStyle: React.CSSProperties = {};
                    if (draggedOverColumnIndex === headerIndex) {
                      dragOverStyle = "border-r-4";
                      dragOverInlineStyle = { borderRightColor: wsColor };
                    }

                    let cursorStyle = "default";
                    if (headerIndex !== 0 && !table.getState().columnSizingInfo.isResizingColumn) {
                      cursorStyle = "grab";
                    }
                    if (isEmpty) {
                      return (
                        <TableHead className="bg-white" key={header.id} style={getHeaderColumnStyle(header.column, header.getSize(), cursorStyle, headerIndex, headerGroup.headers.length)} colSpan={header.colSpan}>
                        </TableHead>
                      )
                    } else {
                      const isLastPinnedColumn = header.id === columnPinning.left?.[columnPinning.left.length - 1] && header.id != "select";
                      return (
                        <TableHead
                          className={TW_DataTableGridHeaderTH + " " + groupHeaderStyle + " " + dragOverStyle + (isLastPinnedColumn ? " border-r-gray-600 border-r-1" : "")}
                          key={header.id}
                          style={{ ...getHeaderColumnStyle(header.column, header.getSize(), cursorStyle, headerIndex, headerGroup.headers.length), ...dragOverInlineStyle }}
                          colSpan={header.colSpan}
                          draggable={headerIndex !== 0 && !table.getState().columnSizingInfo.isResizingColumn}
                          data-column-index={header.index}
                          onDragStart={onDragStart}
                          onDragOver={onDragOver}
                          onDragEnd={onDragEnd}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          <ColumnResizer header={header} isLastColumn={headerIndex === headerGroup.headers.length - 1} guideHeight={tableContentHeight} />
                        </TableHead>
                      )
                    }
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className={TW_DataTableTableBody}>
              {/* Add padding before virtualized content - only when virtualization is enabled */}
              {shouldUseVirtualization && (
                <tr>
                  <td style={{ height: `${Math.max(0, virtualizedBeforeHeight)}px` }} />
                </tr>
              )}
              {/* Render visible rows with optimized rendering */}
              {rows.slice(startIndex, endIndex).map((row) => (
                <TableLine
                  key={row.id}
                  row={row}
                  level={0}
                  valuesForNewRecords={null}
                  groups={groups}
                  groupFields={groupFields}
                  rgbWsColor={rgbWsColor}
                  t={t}
                  selectionState={rowSelection}
                  expandedState={expandedState}
                  columnPinning={columnPinning}
                  groupAggregations={groupAggregations}
                  columnSizing={colSizing}
                  fieldByKeys={fieldByKeys}
                  pathKeys={pathKeys}
                  hasAddAccess={hasAddAccess}
                  hasEditAccess={hasEditAccess}
                  fieldColumns={fieldColumns}
                  pivotField={pivotField ?? null}
                  activeSelectionHandleCell={activeSelectionHandleCell}
                />
              ))}
              {isError && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {t('data.table.invalidField')}
                  </TableCell>
                </TableRow>
              )}
              {rows.length === 0 && fields && fields.length > 0 && !isError && !isAddingRecord && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {t('data.table.noRecords')}
                  </TableCell>
                </TableRow>
              )}
              {/* Add padding after virtualized content - only when virtualization is enabled */}
              {shouldUseVirtualization && (
                <tr>
                  <td style={{ height: `${Math.max(0, virtualizedAfterHeight)}px` }} />
                </tr>
              )}
              {!showAllLines &&
                <TableRow className="bg-transparent hover:bg-transparent">
                  <TableCell colSpan={columns.length}>
                    <p className="w-min bg-white text-center text-gray-400 p-2 text-sm text-nowrap whitespace-nowrap">{t("data.table.not.all.lines.shown")}</p>
                  </TableCell>
                </TableRow>
              }
              {viewSummaries && fields && fields.length !== 0 &&
                <SummaryTableRow table={table} fieldColumns={fieldColumns} summaries={viewSummaries} />
              }
              {hasAddRecordRow &&
                <AddRecordTableRow values={new Map()} className="pr-2" fieldByName={false} displayText={true} disabled={addRecordButtonDisabled} tooltip={addRecordButtonTooltip} fieldColumns={fieldColumns}
                  pinIndex={columnPinning.left ? columnPinning.left.length - 2 : -1} fieldColumnStarts={fieldColumnStarts} onEditionStart={onEditionStart} onEditionEnd={onEditionEnd} />
              }
            </TableBody>
          </Table>
        </div>
        {!view && <AddFieldButton className="sticky ml-1 mr-1 top-[theme('spacing.1')]" style={{ zIndex: 50, left: tableWidth + "px" }} />}
      </div>
    </CellFocusContext.Provider>
  )
}

interface CellHeaderProps {
  table: TSTable<Line>
  fieldColumn: FieldColumn | null
  index: number
  header: Header<Line, Field>
}

function SelectHeader({ table, shift }: CellHeaderProps & { shift: number }) {
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const [isHovered, setIsHovered] = useState(false);
  const isAllSelected = table.getIsAllPageRowsSelected() || false;

  let left = 0;
  if (shift > 0) {
    left += 14 * (shift + 1);
  }

  const selectedStyle = useMemo(() => {
    return {
      left: `${left}px`,
      color: wsColor,
    };
  }, [wsColor])

  const hoverStyle = useMemo(() => {
    return {
      left: `${left}px`,
      color: wsDarkColor,
    };
  }, [wsDarkColor])


  const notSelectedStyle = useMemo(() => {
    return {
      left: `${left}px`,
      color: GRAY_400_COLOR,
    };
  }, [])

  const onMouseEnter = () => {
    setIsHovered(true);
  }

  const onMouseLeave = () => {
    setIsHovered(false);
  }

  const onClick = () => {
    const value = table.getIsAllPageRowsSelected() ? true : false;
    table.toggleAllPageRowsSelected(!value);
  }

  if (isAllSelected) {
    return (
      <CheckSquare onClick={onClick} className={TW_DataTableSelectAllButton_selected} aria-label="Select all" style={isHovered ? hoverStyle : selectedStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
    )
  } else {
    return (
      <Square onClick={onClick} className={TW_DataTableSelectAllButton_not_selected} aria-label="Select all" style={isHovered ? hoverStyle : notSelectedStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
    )
  }
};

interface CellProps {
  row: Row<Line>
  fieldColumn: FieldColumn | null
  index: number
}

interface SelectCellProps extends CellProps {
  table: TSTable<Line>
  // Number of group levels (see lineDepthRef in DataTable)
  lineDepthRef: React.MutableRefObject<number>
}

interface SelectionContextMenuProps {
  open: boolean
  x: number
  y: number
  canPaste: boolean
  canDelete: boolean
  menuRef: React.RefObject<HTMLDivElement>
  onCopy: () => void
  onPaste: () => void
  onDelete: () => void
  onClose: () => void
}

function SelectionContextMenu({
  open,
  x,
  y,
  canPaste,
  canDelete,
  menuRef,
  onCopy,
  onPaste,
  onDelete,
  onClose,
}: SelectionContextMenuProps) {
  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-40 rounded-md border bg-white p-1 shadow-md"
      style={{ left: `${x}px`, top: `${y}px` }}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <button
        className="flex w-full select-none items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-gray-100"
        onClick={() => {
          onCopy();
          onClose();
        }}
      >
        {t("copy.clipboard.menu.copy.cells")}
      </button>
      <button
        className="flex w-full select-none items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50"
        disabled={!canPaste}
        onClick={() => {
          onPaste();
          onClose();
        }}
      >
        {t("copy.clipboard.menu.paste.cells")}
      </button>
      <button
        className="flex w-full select-none items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50"
        disabled={!canDelete}
        onClick={() => {
          onDelete();
          onClose();
        }}
      >
        {t("copy.clipboard.menu.clear.cells")}
      </button>
    </div>,
    document.body
  )
}

function SelectCell({ row, table, lineDepthRef }: SelectCellProps) {
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const [isHovered, setIsHovered] = useState(false);
  const isSelected = row.getIsSelected() || false;

  const selectedStyle = useMemo(() => {
    return {
      color: wsColor,
    };
  }, [wsColor])

  const notSelectedStyle = useMemo(() => {
    return {
      color: GRAY_400_COLOR,
    };
  }, [])

  const hoverStyle = useMemo(() => {
    return {
      color: wsDarkColor,
    };
  }, [wsDarkColor])

  const onMouseEnter = () => {
    setIsHovered(true);
  }

  const onMouseLeave = () => {
    setIsHovered(false);
  }

  // Plain click: toggle the row and anchor it. Shift+click: apply the anchor's
  // state to every visible row between the anchor and this row (see
  // rowRangeSelection.ts).
  const onClick = (e: React.MouseEvent<SVGSVGElement>) => {
    // The store is the source of truth: the table state is injected at render
    // time and lags behind it until the next render.
    const state = useDataStore.getState();
    const next = nextRowSelectionOnClick({
      rowId: row.id,
      shiftKey: e.shiftKey,
      anchor: state.rowRangeAnchor,
      orderedRowIds: visibleRecordRowIds(table.getRowModel().rows, lineDepthRef.current),
      current: state.rowSelection,
    });
    // Routed through onRowSelectionChange: status chip, then the store
    table.setRowSelection(next.rowSelection);
    // Remember the selection object actually stored: a later change made
    // elsewhere replaces it, which invalidates the anchor.
    state.setRowRangeAnchor({ ...next.anchor, applied: useDataStore.getState().rowSelection });
  }

  // Shift+click must not extend the browser's text selection across the rows
  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.shiftKey) {
      e.preventDefault();
    }
  }

  if (isSelected) {
    return <CheckSquare onClick={onClick} onMouseDown={onMouseDown} className={TW_DataTableSelectButton_selected} aria-label="Select row" style={isHovered ? hoverStyle : selectedStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
  } else {
    return <Square onClick={onClick} onMouseDown={onMouseDown} className={TW_DataTableSelectButton_not_selected} aria-label="Select row" style={isHovered ? hoverStyle : notSelectedStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
  }
};

function ShiftHeader({ shift }: { shift: number }) {
  return <TableCell key={"shift-" + shift}
    className="border-none bg-white z-10"
    style={{
      padding: 0,
      width: 10,
      minWidth: 10,
      position: "sticky",
      left: `${shift * 14}px`,
      zIndex: 15,
      backgroundColor: "white",
    }}></TableCell>
}

function ShiftCell({ shift }: { shift: number }) {
  return <TableCell key={"shift-" + shift}
    className="border-none bg-white z-10"
    style={{
      padding: 0,
      width: 10,
      minWidth: 10,
      position: "sticky",
      left: `${shift * 14}px`,
      zIndex: 15,
      backgroundColor: "white",
    }}></TableCell>
}

function renderShift(level: number) {
  const seq: number[] = [];
  for (let i = 0; i < level; i++) {
    seq.push(i);
  }

  return seq.map((i) => {
    let left = 0;
    if (i > 0) {
      left += 14 * i;
    }
    return (
      <TableCell key={"shift-" + i}
        className="border-none bg-white z-10"
        style={{
          padding: 0,
          width: 10,
          minWidth: 10,
          position: "sticky",
          left: `${left}px`,
          zIndex: 15,
          backgroundColor: "white",
        }}></TableCell>
    )
  })
}

function isLeaf(level: number, lineDepth: number): boolean {
  return level === lineDepth - 1;
}

interface TableLineProps {
  row: Row<Line>
  level: number
  valuesForNewRecords: Map<string, any> | null
  groups: Group[]
  groupFields: Field[]
  rgbWsColor: RGBColor
  t: TFunction
  selectionState: RowSelectionState
  expandedState: ExpandedState,
  columnPinning: ColumnPinningState,
  groupAggregations: AggregationType[],
  columnSizing: ColumnSizingState,
  fieldByKeys: Map<string, Field> | null,
  pathKeys: (string | null)[],
  hasAddAccess: boolean,
  hasEditAccess: boolean,
  fieldColumns: FieldColumn[],
  pivotField: Field | null,
  activeSelectionHandleCell: CellPosition | null,
  // Hint : give row selection state and expanded state here
}


function getHeaderColumnStyle(column: Column<Line, unknown>, width: number, cursorStyle: string, headerIndex: number, headerCount: number): React.CSSProperties {
  const isPinned = column.getIsPinned()   // 'left' | 'right' | false
  return {
    position: 'sticky',
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: headerZIndex(headerIndex, headerCount, Boolean(isPinned)),
    width: width,
    cursor: cursorStyle,
  }
}

function getColumnStyle(column: Column<Line, unknown>, width: number, cursorStyle: string, bgColor: string, isLeftGroupCell: boolean = false): React.CSSProperties {
  const isPinned = column.getIsPinned()   // 'left' | 'right' | false
  let left = column.getStart('left');
  let zIndex = 5;
  if (isLeftGroupCell) {
    zIndex = 15;
  } else if (isPinned) {
    zIndex = 7;
  }
  return {
    position: isPinned ? 'sticky' : 'relative',
    left: isPinned === 'left' ? `${left}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: zIndex,           // keep it above scrolling cells
    background: (isLeftGroupCell ? "white" : bgColor),                // cover content that scrolls under
    width: width,
    cursor: cursorStyle,
  }
}

function getSelectionHandleCellStyle(column: Column<Line, unknown>, bgColor: string, isLeftGroupCell: boolean = false): React.CSSProperties {
  const baseStyle = getColumnStyle(column, column.getSize(), "default", bgColor, isLeftGroupCell);
  const isPinned = column.getIsPinned();

  return {
    ...baseStyle,
    zIndex: isLeftGroupCell || isPinned ? 8 : 6,
  };
}

function getGroupRowStyle(level: number, isIcon: boolean): React.CSSProperties {
  let left = isIcon ? 12 : 40;
  if (level > 0) {
    left += 14 * (level + 1);
  }
  return {
    position: "sticky",
    left: `${left}px`,
    zIndex: 5,
  }
}

function getAddRecordButtonStyle(level: number, isLeftGrouped: boolean, childrenNb: number = 0, isLineHovered: boolean = false): React.CSSProperties {
  if (!isLeftGrouped) {
    let left = 2;
    if (level > 0) {
      left += 14 * (level + 1);
    }
    return {
      position: "sticky",
      left: `${left}px`,
      zIndex: 15,
      paddingRight: "0.5rem",
    };
  } else {
    const top = childrenNb * (minRowHeight / 2) * 0.25 + (Math.floor(childrenNb / 2)) * 0.25 - 4 * 0.25;
    const right = -4 * 0.25;
    return {
      position: "absolute",
      right: right + "rem",
      top: top + "rem",
      zIndex: 15,
      opacity: isLineHovered ? 1 : 0,
      transition: "opacity 0.2s ease-in-out",
      borderRadius: "calc(infinity * 1px)",
    };
  }
}

function getNoChildrenTextStyle(childrenNb: number = 0, isLineHovered: boolean = false): React.CSSProperties {

  const top = childrenNb * (minRowHeight / 2) * 0.25 + (Math.floor(childrenNb / 2)) * 0.25 - 2.5 * 0.25;
  const right = -135 * 0.25;
  return {
    position: "absolute",
    right: right + "rem",
    top: top + "rem",
    zIndex: 15,
    opacity: isLineHovered ? 1 : 0,
    transition: "opacity 0.2s ease-in-out",
  };
}

const expandOnClick = function (row: Row<Line>) {
  row.getToggleExpandedHandler()();
};

// Function to count the number of leaf lines (actual data rows) in a group
const countLinesInGroup = (row: Row<Line>, lineDepth: number, level: number, groupValue?: string, t?: TFunction): number => {

  // Check if this is a leaf line (actual data row) by checking if it's at the deepest level
  if (level >= lineDepth) {
    return 1;
  }

  // If this row has no subRows, it means it's empty (no actual data)
  if (!row.subRows || row.subRows.length === 0) {
    return 0;
  }

  // Recursively count all leaf lines in subRows
  return row.subRows.reduce((total, subRow) => {
    return total + countLinesInGroup(subRow, lineDepth, level + 1, groupValue, t);
  }, 0);
};

const TableLine = memo(
  function TableLine({ row, level, valuesForNewRecords, groups, groupFields, rgbWsColor, t, selectionState, expandedState, columnPinning, groupAggregations, columnSizing, fieldByKeys, pathKeys, hasAddAccess, hasEditAccess, fieldColumns, pivotField, activeSelectionHandleCell }: TableLineProps) {
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const relationByKeys = useModelStore(state => state.relationByKeys);

    const lineDepth = groups?.length ?? 0;

    const onMouseOver = useCallback((e: React.MouseEvent<HTMLTableRowElement>) => {
      const id = e.currentTarget.dataset.rowId;
      setIsHovered(true);
      setHoveredId(id ?? null);
    }, []);

    const onMouseOut = useCallback(() => {
      setIsHovered(false);
      setHoveredId(null);
    }, []);

    function leftGroupedCell(parent: Row<Line>, child: Row<Line>, level: number, childIndex: number, childrenNb: number, hasNoChildren: boolean, pathKeys: (string | null)[], rowSpanByPathKeys: Map<string, number>, lineBgColor: string | undefined, valuesForNewLines: Map<string, any> | null): React.ReactNode {
      return (
        <>
          {child.getVisibleCells().map((cell, cellIndex) => {
            const pathIndex = cellIndex - (level + 1);
            let borderClass = TW_DataTableBorder + " p-0";
            if (cell.column.getIndex() < lineDepth - 1) {
              borderClass = "border-0";
            } else if (cell.column.getIndex() === 0) {
              borderClass += " border-l-0 ";
            }
            if (childIndex !== 0 || pathIndex > level) {
              borderClass += " overflow-visible text-ellipsis text-nowrap whitespace-nowrap";
            }
            const isLastPinnedColumn = cell.column.id === columnPinning.left?.[columnPinning.left.length - 1] && cell.column.id != "select";
            const isSelectionHandleCell = activeSelectionHandleCell?.rowId === child.id && activeSelectionHandleCell?.column === cell.column.getIndex() - 1;
            let rowSpan = 1;
            let isLeftGroupCell = childIndex === 0 && pathIndex === 0;
            const pathKey = pathIndex >= 0 ? pathKeys[pathIndex] : null;
            if (pathKey) {
              const pathFirstLink = extractFirstLinkPath(pathKey);
              const pathKeyIndex = Array.from(rowSpanByPathKeys.keys()).find((key) => {
                const keyFirstLink = extractFirstLinkPath(key);
                return pathKey === key || (pathFirstLink && keyFirstLink && pathFirstLink === keyFirstLink);
              });
              if (pathKeyIndex) {
                rowSpan = rowSpanByPathKeys.get(pathKeyIndex) || 1;
              }
            }
            if (rowSpan > 1 && childIndex > 0) {
              return null;
            }

            let bgColor: string | undefined = undefined;
            let weekendBgColor = undefined;
            // cellIndex - 1 since there is the select column and shift columns in case of grouped rows
            const fieldColumn = fieldColumns[cellIndex - (level + 1)];
            if (fieldColumn?.isPivot && pivotField?.type === FieldType.Date && fieldColumn.pivotValue?.value) {
              const pivotDateString = fieldColumn.pivotValue.value.toString();
              const pivotDate = new Date(pivotDateString);
              const dayOfWeek = pivotDate.getDay(); // 0 = Sunday, 6 = Saturday
              if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
                weekendBgColor = WEEKEND_COLOR;
              }
            }
            bgColor = lineBgColor ?? weekendBgColor ?? "white";


            if (!valuesForNewLines) {
              valuesForNewLines = new Map<string, any>();
            }
            var addTooltip = undefined;
            if (!hasAddAccess) {
              addTooltip = t("data.table.no.add.access.adding.record.disabled");
            }
            return (
              <TableCell key={cell.id}
                className={borderClass + (isLastPinnedColumn ? " border-r-gray-600" : "")}
                style={isSelectionHandleCell
                  ? getSelectionHandleCellStyle(cell.column, bgColor, isLeftGroupCell)
                  : getColumnStyle(cell.column, cell.column.getSize(), "default", bgColor, isLeftGroupCell)}
                rowSpan={rowSpan}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                {isLeftGroupCell &&
                  <AddRecordButton parentRowId={parent.id} values={valuesForNewLines} fieldByName={false} style={getAddRecordButtonStyle(level, parent.original.isLeftGrouped, childrenNb, isHovered)} displayText={false} lineHovered={isHovered} disabled={!hasAddAccess} tooltip={addTooltip} />
                }
                {isLeftGroupCell && hasNoChildren &&
                  <p className="text-gray-500 text-sm w-min text-nowrap text-left bg-white rounded-lg px-2" style={getNoChildrenTextStyle(childrenNb, isHovered)}>{t('data.table.leftGroup.noChildren')}</p>
                }
              </TableCell>
            )
          })}
        </>
      )
    }

    if (level < lineDepth) {
      const cell = row.getAllCells()[0];
      let rounded = " rounded-lg";
      if (isLeaf(level, lineDepth) && row.getIsExpanded()) {
        rounded = " rounded-t-lg ";
      }
      const groupPathKey = groups[level].pathKey;
      const groupField = groupFields[level];
      if (!groupField) {
        return null;
      }
      const groupPath = fieldToPath(selectedObjectInfos!.object._key, relationByKeys, fieldByKeys, groupField);
      if (!groupPath) {
        return null;
      }
      const groupItem = row.original.items[0];

      let valuesForNewLines = valuesForNewRecords;
      if (!valuesForNewLines) {
        valuesForNewLines = new Map<string, any>();
      }
      if (groupField) {
        if (groupField.type === FieldType.Link) {
          if (groupItem.key && groupItem.key.recordKey && groupPath?.edges.length === 1) {
            valuesForNewLines.set(groupField._key, groupItem.key.recordKey);
          }
        } else {
          if (groupItem.value && groupItem.value !== "") {
            valuesForNewLines.set(groupField._key, groupItem.value);
          }
        }
      }

      const bgColor = `rgba(${rgbWsColor.red}, ${rgbWsColor.green}, ${rgbWsColor.blue}, ${1 - level * 0.4})`;
      const groupAggregation = groupAggregations[level];
      const format = selectedView?.formats?.get(groupPathKey ?? "") ?? groupField?.format;
      const groupValue = itemToString(groupItem, groupField, groupAggregation, format, t, t('data.table.emptyGroup'), fieldByKeys);

      // Summaries of this group (server-computed, index-aligned with the
      // final columns): one entry per column, displayed under its column —
      // unless the group's configuration hides them
      const lineSummaries = groups[level]?.hideSummaries ? null : row.original.summaries;
      let summaryCells: { aggregation: AggregationType | null, formatted: string, summarized: boolean }[] | null = null;
      if (lineSummaries) {
        const summaryAggregations = summaryAggregationsByColumns(selectedView?.summaries, fieldColumns);
        summaryCells = fieldColumns.map((fieldColumn, index) => {
          const aggregation = summaryAggregations[index];
          const formatted = aggregation ? summaryValueToString(lineSummaries[index], fieldColumn, aggregation, columnAggregation(fieldColumn.field, selectedView?.aggregations, fieldColumn.pathKey), selectedView?.formats, fieldByKeys, t) : "";
          return { aggregation, formatted, summarized: aggregation != null && formatted !== "" };
        });
      }
      const hasGroupSummaries = summaryCells != null && summaryCells.some(cell => cell.summarized);

      if (row.original.isLeftGrouped) {
        const childrenNb = row.subRows.length;
        const hasNoChildren = childrenNb === 1 && !row.subRows[0].original.items.some((item, index) => index > level && !item.readOnly);
        const rowSpanByPathKeys = new Map<string, number>();
        const leftGroupsNb = 1;
        if (!hasNoChildren) {
          const firstChild = row.subRows[0];
          firstChild?.getVisibleCells()?.forEach((cell, cellIndex) => {
            if (cellIndex > level) {
              const pathIndex = cellIndex - (level + 1);
              const pathKey = pathKeys[pathIndex];
              let isLeftGroupCell = pathIndex < leftGroupsNb;
              if (isLeftGroupCell && pathKey) {
                rowSpanByPathKeys.set(pathKey, childrenNb);
              }
            }
          });
        }

        return (
          <>
            {row.subRows.map((child, childIndex) => {
              let bgColor: string | undefined = undefined;
              if (hoveredId === child.id) {
                bgColor = LINE_HOVER_COLOR;
              }

              return (
                <TableRow
                  key={child.id}
                  data-row-id={child.id}
                  data-state={child.getIsSelected() && "selected"}
                  className={TW_DataTableTableBodyRow}
                  onMouseOver={onMouseOver}
                  onMouseOut={onMouseOut}
                >
                  {leftGroupedCell(row, child, level, childIndex, childrenNb, hasNoChildren, pathKeys, rowSpanByPathKeys, bgColor, valuesForNewLines)}
                </TableRow>
              )
            })}
            {hasGroupSummaries && summaryCells &&
              <TableRow key={row.id + "-summary"} className="h-9 hover:bg-transparent">
                {row.subRows[0].getVisibleCells().map((cell, cellIndex) => {
                  const pathIndex = cellIndex - (level + 1);
                  const summaryCell = pathIndex >= 0 ? summaryCells![pathIndex] : undefined;
                  const summarized = summaryCell?.summarized ?? false;
                  // Contiguous summarized cells merge into one grey band,
                  // rounded at its extremities; the other cells stay
                  // transparent. A tr cannot carry a margin: the gap between
                  // groups is a transparent bottom border, with the band
                  // background clipped to the padding box.
                  const style: React.CSSProperties = {
                    ...getColumnStyle(cell.column, cell.column.getSize(), "default", summarized ? GROUP_BASE_COLOR : "transparent", false),
                    borderRadius: summaryCellRadius(summarized, summaryCells![pathIndex - 1]?.summarized ?? false, summaryCells![pathIndex + 1]?.summarized ?? false),
                    borderBottom: "4px solid transparent",
                    backgroundClip: "padding-box",
                  };
                  return (
                    <TableCell key={row.id + "-summary-" + cellIndex} className="p-0" style={style}>
                      {summarized && summaryCell &&
                        <div className="flex items-center justify-end px-2 h-8" title={t("field.aggregation.label." + summaryCell.aggregation)}>
                          <span className="text-sm font-semibold text-gray-800">{summaryCell.formatted}</span>
                        </div>
                      }
                    </TableCell>
                  );
                })}
              </TableRow>
            }
          </>
        )
      } else {
        return (
          <>
            <TableRow
              key={row.id}
              data-row-id={row.id}
              data-state={row.getIsSelected() && "selected"}
              className={TW_DataTableTableBodyRow}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            >
              {renderShift(level)}
              <TableCell key={cell.id}
                colSpan={row.getAllCells().length}
                className="p-0">
                <div className={TW_DataTableGroupRow + rounded + (level === 0 ? " mt-1" : " mt-0.5")} onClick={() => expandOnClick(row)}
                  style={{ backgroundColor: bgColor }}>
                  {row.getIsExpanded() ? <ChevronDown className={TW_DataTableGroupRowIcon} style={getGroupRowStyle(level, true)} /> : <ChevronRight className={TW_DataTableGroupRowIcon} style={getGroupRowStyle(level, true)} />}
                  <div className={TW_DataTableGroupRowTextContainer} style={getGroupRowStyle(level, false)}>
                    {(groupValue === t('data.table.emptyGroup')) ?
                      <p className={TW_DataTableGroupRowTextEmpty}>{t('data.table.emptyGroup')}</p> :
                      <p className={TW_DataTableGroupRowText}>{groupValue} <span className="text-gray-400 text-xs pl-1">({countLinesInGroup(row, lineDepth, level, groupValue, t)})</span></p>
                    }
                    {groupField && <p className={TW_DataTableGroupFieldName}>{groupField.name}</p>}
                  </div>
                  {hasGroupSummaries && summaryCells && (() => {
                    // The group band is a single spanning cell: the summary of
                    // each column is absolutely positioned at the column's
                    // offset (minus the shift cells rendered before the band)
                    const allCells = row.getAllCells();
                    const shiftOffset = allCells.slice(0, level).reduce((acc, shiftCell) => acc + shiftCell.column.getSize(), 0);
                    const fieldCells = allCells.filter(c => c.column.id !== "select" && !c.column.id.startsWith("group-"));
                    return fieldCells.map((fieldCell, index) => {
                      const summaryCell = summaryCells![index];
                      if (!summaryCell?.summarized) {
                        return null;
                      }
                      return (
                        <div key={row.id + "-group-summary-" + index}
                          className="absolute top-0 h-full flex items-center justify-end px-2"
                          style={{ left: fieldCell.column.getStart() - shiftOffset, width: fieldCell.column.getSize() }}
                          title={t("field.aggregation.label." + summaryCell.aggregation)}>
                          <span className="text-sm font-semibold text-gray-800">{summaryCell.formatted}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </TableCell>
            </TableRow>
            {row.getIsExpanded() && row.subRows.map((child) => {
              return <TableLine key={child.id} row={child} level={level + 1} valuesForNewRecords={valuesForNewLines} groups={groups} groupFields={groupFields} rgbWsColor={rgbWsColor} t={t} selectionState={selectionState} expandedState={expandedState} columnPinning={columnPinning} groupAggregations={groupAggregations} columnSizing={columnSizing} fieldByKeys={fieldByKeys} pathKeys={pathKeys} hasAddAccess={hasAddAccess} hasEditAccess={hasEditAccess} fieldColumns={fieldColumns} pivotField={pivotField ?? null} activeSelectionHandleCell={activeSelectionHandleCell} />
            })}
            {hasAddAccess && (isLeaf(level, lineDepth)) && row.getIsExpanded() &&
              <TableRow
                data-state={row.getIsSelected() && "selected"}
                className={TW_DataTableTableLastGroupRow}>
                {renderShift(level)}
                <TableCell colSpan={row.getAllCells().length - level} className={TW_DataTableTableLastGroupRowCell}>
                  <AddRecordButton parentRowId={row.id} values={valuesForNewLines} fieldByName={false} style={getAddRecordButtonStyle(level, row.original.isLeftGrouped)} displayText={true} />
                </TableCell>
              </TableRow>
            }
          </>
        )
      }
    } else {

      return (
        <TableRow
          key={row.id}
          data-row-id={row.id}
          data-state={row.getIsSelected() && "selected"}
          className={TW_DataTableTableBodyRow}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        >
          {row.getVisibleCells().map((cell, index) => {
            let bgColor: string | undefined = undefined;
            if (hoveredId === row.id) {
              bgColor = LINE_HOVER_COLOR;
            }
            let borderClass = TW_DataTableBorder;
            if (cell.column.getIndex() < lineDepth - 1) {
              borderClass = "border-0";
            } else if (cell.column.getIndex() === 0) {
              borderClass += " border-l-0 ";
            }
            borderClass += " overflow-visible text-ellipsis text-nowrap whitespace-nowrap p-0";
            const isLastPinnedColumn = cell.column.id === columnPinning.left?.[columnPinning.left.length - 1] && cell.column.id != "select";
            // Check if this is a pivot column representing a weekend date
            let weekendBgColor = undefined;
            // cellIndex - 1 since there is the select column and shift columns in case of grouped rows
            const fieldColumn = fieldColumns[index - Math.max(1, level)];
            if (fieldColumn?.isPivot && pivotField?.type === FieldType.Date && fieldColumn.pivotValue?.value) {
              const pivotDateString = fieldColumn.pivotValue.value.toString();
              const pivotDate = new Date(pivotDateString);
              const dayOfWeek = pivotDate.getDay(); // 0 = Sunday, 6 = Saturday
              if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
                weekendBgColor = WEEKEND_COLOR;
              }
            }
            bgColor = bgColor ?? weekendBgColor ?? "white";
            const isSelectionHandleCell = activeSelectionHandleCell?.rowId === row.id && activeSelectionHandleCell?.column === cell.column.getIndex() - 1;
            return (
              <TableCell key={cell.id}
                className={borderClass + (isLastPinnedColumn ? " border-r-gray-600" : "")}
                style={isSelectionHandleCell
                  ? getSelectionHandleCellStyle(cell.column, bgColor)
                  : getColumnStyle(cell.column, cell.column.getSize(), "default", bgColor)}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            )
          })}
        </TableRow>
      )
    }
  }
  , (prevProps, nextProps) => {
    // Optimized memo comparison - check most likely to change props first
    if (prevProps.level !== nextProps.level) {
      return false;
    }

    // Quick row ID check
    if (prevProps.row.id !== nextProps.row.id) {
      return false;
    }

    // Check if row data has changed
    if (!areRowsEqual(prevProps.row, nextProps.row)) {
      return false;
    }

    // Check table state changes
    if (!tableStateEqual(nextProps.row, prevProps.expandedState, nextProps.expandedState, prevProps.selectionState, nextProps.selectionState, prevProps.columnPinning, nextProps.columnPinning, prevProps.columnSizing, nextProps.columnSizing)) {
      return false;
    }

    // Check other props that might affect rendering
    if (prevProps.hasEditAccess !== nextProps.hasEditAccess ||
      prevProps.groups.length !== nextProps.groups.length) {
      return false;
    }

    if (prevProps.pivotField !== nextProps.pivotField) {
      return false;
    }

    if (prevProps.groupFields.length !== nextProps.groupFields.length) {
      return false;
    }

    if (prevProps.groups.some((group, index) =>
      group.pathKey !== nextProps.groups[index].pathKey ||
      group.sort !== nextProps.groups[index].sort ||
      group.hideEmptyGroups !== nextProps.groups[index].hideEmptyGroups ||
      group.displayType !== nextProps.groups[index].displayType ||
      group.applyGroupFilter !== nextProps.groups[index].applyGroupFilter ||
      group.filter !== nextProps.groups[index].filter ||
      (group.hideSummaries ?? false) !== (nextProps.groups[index].hideSummaries ?? false))) {
      return false;
    }

    if ((prevProps.valuesForNewRecords !== null && nextProps.valuesForNewRecords === null)
      || (prevProps.valuesForNewRecords === null && nextProps.valuesForNewRecords !== null)
      || (prevProps.valuesForNewRecords !== null && nextProps.valuesForNewRecords !== null && prevProps.valuesForNewRecords.size !== nextProps.valuesForNewRecords.size)) {
      return false;
    }

    if (prevProps.rgbWsColor !== nextProps.rgbWsColor) {
      return false;
    }

    if (prevProps.columnPinning !== nextProps.columnPinning) {
      return false;
    }

    if (prevProps.columnSizing !== nextProps.columnSizing) {
      return false;
    }

    if (prevProps.activeSelectionHandleCell?.rowId !== nextProps.activeSelectionHandleCell?.rowId
      || prevProps.activeSelectionHandleCell?.column !== nextProps.activeSelectionHandleCell?.column) {
      return false;
    }

    return true;
  });

function tableStateEqual(row: Row<Line>, oldExpanded: ExpandedState, newExpanded: ExpandedState, oldSelection: RowSelectionState, newSelection: RowSelectionState, oldColumnPinning: ColumnPinningState, newColumnPinning: ColumnPinningState, oldColumnSizing: ColumnSizingState, newColumnSizing: ColumnSizingState): boolean {
  const rowId = row.id;
  if (oldExpanded === true && newExpanded !== true) {
    return false;
  } else if (oldExpanded !== true && newExpanded === true) {
    return false;
  } else {
    const oldExpandedRecord = oldExpanded as Record<string, boolean>;
    const newExpandedRecord = newExpanded as Record<string, boolean>;
    if (oldExpandedRecord[rowId] !== newExpandedRecord[rowId]) {
      return false;
    }
    if (oldSelection[rowId] !== newSelection[rowId]) {
      return false;
    }
    if (oldColumnPinning !== newColumnPinning) {
      return false;
    }
    if (oldColumnSizing !== newColumnSizing) {
      return false;
    }
  }
  const equalSubRows = row.subRows.every((child) => tableStateEqual(child, oldExpanded, newExpanded, oldSelection, newSelection, oldColumnPinning, newColumnPinning, oldColumnSizing, newColumnSizing));
  return equalSubRows;
}

function areRowsEqual(row1: Row<Line>, row2: Row<Line>): boolean {
  return row1.id === row2.id &&
    areLinesEqual(row1.original, row2.original, false) &&
    row1.subRows.length === row2.subRows.length &&
    row1.subRows.every((child1, index) => areRowsEqual(child1, row2.subRows[index]));
}
